import React, { useEffect, useMemo, useState } from "react";
import styles from "./EditCategoryModal.module.css";
import API from "../../../utils/api";
import RenderToast from "../../../components/RenderToast";

const slugify = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

export default function EditCategoryModal({ category, onClose }) {
  const [categoryName, setCategoryName] = useState(category.categoryName);
  const [parentId, setParentId] = useState(category.parent_id || "");
  const [slug, setSlug] = useState(category.category_slug);
  const [description, setDescription] = useState(category.description || "");
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setCategoryName(category.categoryName);
    setParentId(category.parent_id || "");
    setSlug(category.category_slug);
    setDescription(category.description || "");
  }, [category]);

  useEffect(() => {
    (async () => {
      try {
        const res = await API.post("/categories.php", { action: "fetch" });
        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const normalized = data.map((c) => ({
          ...c,
          is_uncategorized: Number(c.is_uncategorized) === 1,
        }));
        setCategories(
          normalized.filter((c) => c.id !== category.id && !c.is_uncategorized)
        );
      } catch (err) {
        console.error(err);
        RenderToast({ type: "error", message: "Unable to load categories." });
      }
    })();
  }, [category.id]);

  useEffect(
    () => setSlug(categoryName ? slugify(categoryName) : ""),
    [categoryName]
  );

  const parentOptions = useMemo(() => {
    const parentMap = {};
    categories.forEach((cat) => {
      if (!cat.parent_id) parentMap[cat.id] = { ...cat, children: [] };
    });
    categories.forEach((cat) => {
      if (cat.parent_id && parentMap[cat.parent_id])
        parentMap[cat.parent_id].children.push(cat);
    });
    return Object.values(parentMap);
  }, [categories]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("authToken");
    if (!token)
      return RenderToast({ type: "error", message: "Please login first" });

    try {
      setSaving(true);
      const formData = new URLSearchParams();
      formData.append("action", "update");
      formData.append("id", category.id);
      formData.append("category_name", categoryName.trim());
      formData.append("parent_id", parentId || "");
      formData.append("slug", slug);
      formData.append("description", description.trim());

      const res = await fetch(`${API.base}/categories.php`, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData,
      });

      const data = await res.json();
      if (data.success) {
        RenderToast({
          type: "success",
          message: "Category updated successfully!",
        });
        onClose();
      } else {
        RenderToast({
          type: "error",
          message: data.error || "Failed to update category",
        });
      }
    } catch {
      RenderToast({
        type: "error",
        message: "Network error. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3>Edit Category</h3>
          <button className={styles.closeBtn} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className={styles.content}>
          <form className={styles.form} onSubmit={handleUpdate}>
            <label>
              Category Name *
              <input
                type="text"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                required
              />
            </label>

            <label>
              Slug
              <input type="text" value={slug} disabled />
            </label>

            <label>
              Parent Category
              <select
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
              >
                <option value="">None</option>
                {parentOptions.map((parent) => (
                  <React.Fragment key={parent.id}>
                    <option value={parent.id}>{parent.categoryName}</option>
                    {parent.children.map((child) => (
                      <option key={child.id} value={child.id}>
                        {`${parent.categoryName} → ${child.categoryName}`}
                      </option>
                    ))}
                  </React.Fragment>
                ))}
              </select>
            </label>

            <label>
              Description
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional"
              />
            </label>

            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={styles.btnPrimary}
                disabled={saving}
              >
                {saving ? "Saving..." : "Update"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
